import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { extractUUIDTail } from "./uuidTextConverter";

export type headerTitleParts = {
  title?: string,
  prefix?: string,
  suffix?: string,
}

export function useLoginPageTitle() {
  useEffect(() => {
    document.title = "ログイン";
    return () => {
      document.title = "材料管理システム";
    };
  }, []);
}
export function usePageTitle() {
  const location = useLocation();
  useEffect(() => {
    document.title = `${getPageTitle(new URL(location.pathname, window.location.origin))}`;
    return () => {
      document.title = "不明なページ";
    };
  }, [location]);
}

export function getPageTitle(url: URL) {
  const pageTitles = getPageTitles(url);
  return pageTitles.prefix + pageTitles.title + pageTitles.suffix;
}

// id 候補を「ヘッダー表示用の短縮 ID」へ変換する。
//   - 数値 ID: そのまま文字列化
//   - UUID v4 形式: extractUUIDTail(最後の16進フィールド → 15桁0埋め10進)
//   - シード由来 prefixed ID('iss--000000000001' 等): ハイフン区切りで分解し、空セグメントを除いた最後の有意セグメント
//   - その他文字列: そのまま
// rev: uuidTextConverter.ts に idForHeader が未実装のため、usePageTitle 内にインライン実装する。
const UUID_RE_FOR_HEADER = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function idForHeader(id: string): string {
  if (id === "") return "";
  if (UUID_RE_FOR_HEADER.test(id)) return extractUUIDTail(id);
  const parts = id.split("-").filter((p) => p !== "");
  if (parts.length >= 2) return parts[parts.length - 1] ?? id;
  return id;
}

// id 候補が UUID / prefixed-id / 純粋な数値 のいずれかに該当するかを判定
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NUMERIC_RE = /^[0-9]+$/;
const PREFIXED_ID_RE = /^[A-Za-z]+(?:-+|_)[A-Za-z0-9-]+$/; // 'iss--0001' / 'co---0001' / 'so-k-1' 等
const HAS_DIGIT_RE = /[0-9]/;
function looksLikeId(s: string): boolean {
  if (!s) return false;
  // 数値 ID と UUID はそのまま採用。
  // prefixed-id は「英字＋区切り＋…」形だが、これだけだと "construction-execute" や
  // "carry-out" のような『ハイフン区切りのルート語』（= ページ名そのもの。ID ではない）にも
  // 誤マッチし、ヘッダー/タブに " execute" / " out" 等の英語が混入してしまう。
  // 実データの prefixed-id は必ず数字を含む一方、ルート語は数字を含まないため、
  // prefixed 判定に「数字を1つ以上含む」ことを必須化して両者を分離する。
  return UUID_RE.test(s) || NUMERIC_RE.test(s) || (PREFIXED_ID_RE.test(s) && HAS_DIGIT_RE.test(s));
}

// URL 末尾セグメントの解釈:
//   - 'new'                          → prefix='新規'
//   - 'edit' / 'scan' / 'process'    → 動作キーワード。1つ手前のセグメントを ID 候補として扱う
//   - UUID / prefixed-ID / 数値      → suffix = ' {idForHeader(...)}'
export function getPageTitles(url: URL): { title: string, prefix: string, suffix: string } {
  const segments = url.pathname.split("/").filter(segment => segment !== "");
  const last = segments[segments.length - 1] ?? "";
  const secondLast = segments[segments.length - 2] ?? "";

  let prefix = "";
  let suffix = "";

  if (last === "new") {
    prefix = "新規";
  } else {
    const idCandidate = (last === "edit" || last === "scan" || last === "process")
      ? secondLast
      : last;
    if (idCandidate && idCandidate !== "new" && looksLikeId(idCandidate)) {
      suffix = ` ${idForHeader(idCandidate)}`;
    }
  }

  return { title: getPageName(url), prefix, suffix };
}

function getPageName(url: URL) {
  const path = url.pathname;

  // ネストがあるパスの場合
  if (path.startsWith("/console/construction-plan")) {
    // /construction-plan/area/... → 施工箇所、それ以外 → 施工管理
    const childPath = path.slice("/console/construction-plan".length);
    if (childPath.startsWith("/area")) return "施工箇所";
    return "施工管理";
  }
  if (path.startsWith("/console/carry-out-attachment")) {
    return "貼付履歴";
  }
  if (path.startsWith("/console/carry-out")) {
    const childPath = path.slice("/console/carry-out".length);
    if (childPath.includes("/lot")) return "材料ロット";
    return "出荷";
  }
  if (path.startsWith("/console/search-materials")) {
    if (path.endsWith("/console/search-materials/")) return "材料検索";
    return "材料";
  }
  if (path.startsWith("/console/settings")) {
    if (path.endsWith("/console/settings/")) return "設定一覧";
    if (path.startsWith("/console/settings/cache")) return "工事情報取得";
    return "設定";
  }
  if (path.startsWith("/console/masters")) {
    // /masters/ → マスター編集一覧、 /masters/:key/:id/ → 個別マスタ閲覧。後者はページ側で上書きされる想定。
    if (path === "/console/masters/" || path === "/console/masters") return "マスター編集";
    return "マスタ";
  }
  if (path.startsWith("/console/work-master")) return "工事情報入力";
  if (path.startsWith("/console/construction-execute")) return "施工実施";
  if (path.startsWith("/console/quality")) return "品質測定";
  if (path.startsWith("/console/quantity-plan")) return "数量計画";
  if (path.startsWith("/console/reports")) return "帳票出力";
  if (path.startsWith("/console/stock")) return "発注・在庫";

  // ネストがないパス
  if (path.startsWith("/console/issuance")) return "発行";
  if (path.startsWith("/console/carry-in")) return "搬入";

  return "材料管理システム";
}
